import React, { useEffect, useState } from 'react';
import { Constants } from "@eagerdog/constants";
import { IShowRegistrationSummary, IEvent, IShow } from "@eagerdog/interfaces";
import { AxiosError } from 'axios';
import moment from "moment";

import { toast } from "src/components/Toast/ToastManager";
import { apiService } from "../../services/api.service";

import Button from "src/components/Button/Button";
import FormBuilder, { IDogClassElement } from "src/components/FormBuilder/FormBuilder";
import Confirm from "src/components/Confirm/Confirm";

import './ChangeElements.scss';

interface IProps {
  event: IEvent | undefined,
  registration: IShowRegistrationSummary,
  onComplete(): void,
  onClose(): void
}

interface IShowClass extends IShow {
  dog_classes?: any[]
}

const ChangeElements: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [refreshTimeslots, setRefreshTimeslots] = useState<number>(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [showUKCConfirmationModal, setShowUKCConfirmationModal] = useState<boolean>(false);
  const [updateUKCRegistration, setUpdateUKCRegistration] = useState<boolean>(true);

  const [scoreIds, setScoreIds] = useState<string[]>([]);
  const [originalScoreIds, setOriginalScoreIds] = useState<string[]>([]); // Never changes after initial load
  const [shows, setShows] = useState<IShowClass[]>();

  const isValidClasses = () => {
    if (!shows) return true;
    
    let _shows = JSON.parse(JSON.stringify(shows));

    // Validate Nosework sections
    if (props.registration.show_type === Constants.show_type.nosework) {
      for (let s in _shows) {
        for (let c in _shows[s].dog_classes) {
          // Skip validation for classes that don't exist in show_elements (marked as unavailable)
          const classExists = _shows[s].show_elements?.some((se: any) => 
            se.show_element === _shows[s].dog_classes[c].show_element &&
            se.level === _shows[s].dog_classes[c].level
          );
          
          // Only validate section if the class actually exists in the show
          if (classExists && _shows[s].dog_classes[c].section !== undefined && _shows[s].dog_classes[c].section === "None") {
            toast.show({
              title: "Change Elements",
              content: "Please select a section for " + _shows[s].dog_classes[c].show_element + " ("+ _shows[s].show_type + " " + (_shows[s].custom_show_name || _shows[s].show_name) + " " + moment(_shows[s].show_date).format("MMMM Do") +")",
              duration: 10000,
              type: "fail"
            });

            return false;
          }
        }
      }
    }

    // Validate timeslot requirements for non-Fast Cat timeslot events
    const isTimeslotEvent = [Constants.show_type.fast_cat, Constants.show_type.fetch].includes(props.registration.show_type);
    const isNonFastCatTimeslotEvent = isTimeslotEvent && props.registration.show_type !== Constants.show_type.fast_cat;
    
    if (isNonFastCatTimeslotEvent) {
      const classCount = getClassCount();
      let hasTimeslot = false;
      
      // Check if any classes have timeslots
      for (let s in _shows) {
        for (let c in _shows[s].dog_classes) {
          if (_shows[s].dog_classes[c].time_slot) {
            hasTimeslot = true;
            break;
          }
        }
        if (hasTimeslot) break;
      }
      
      // If there are classes but no timeslot, that's invalid
      if (classCount > 0 && !hasTimeslot) {
        toast.show({
          title: "Change Elements",
          content: `Please select a timeslot for your ${props.registration.show_type} registration. Classes require a timeslot.`,
          duration: 10000,
          type: "fail"
        });
        return false;
      }
    }

    return true;
  }

  const getClassCount = () => {
    let amt:number = 0;
    let _shows = JSON.parse(JSON.stringify(shows));
    
    for (let s in _shows) {
      // Only count classes that have both show_element and level defined
      // This excludes partial selections where user selected an element but set level to "None"
      for (let c in _shows[s].dog_classes) {
        const dogClass = _shows[s].dog_classes[c];
        if (dogClass.show_element && dogClass.level) {
          amt++;
        }
      }
    }

    return amt;
  }

  const submitChange = () => {
    if (props.event) {
      // First validate classes (including timeslot requirements)
      if (!isValidClasses()) {
        return;
      }
      
      const classCount = getClassCount();
      const originalCount = originalScoreIds.length; // Use originalScoreIds which never changes
      const isFastCat = props.registration.show_type === Constants.show_type.fast_cat;
      
      // For Fast Cat, allow removing timeslots (fewer classes than original)
      // For other show types, require exact match
      const isValidCount = isFastCat ? classCount <= originalCount : classCount === originalCount;
      
      if (isValidCount) {
        // For AKC, PWDCA, and ABI events, show warning about score deletion
        if (props.event.sanctioning_club === Constants.sanctioning_club.AKC || 
            props.event.sanctioning_club === Constants.sanctioning_club.PWDCA ||
            props.event.sanctioning_club === Constants.sanctioning_club.ABI) {
          setShowConfirmationModal(true);
          return;
        }
        
        // For UKC events, show confirmation about updating initial registration
        if (props.event.sanctioning_club === Constants.sanctioning_club.UKC) {
          setShowUKCConfirmationModal(true);
          return;
        }
        
        // For other sanctioning clubs, proceed directly
        performUKCElementChange();
      } else {
        const errorMessage = isFastCat 
          ? `Fast Cat: You can select up to ${originalCount} timeslots (currently have ${classCount})`
          : `Please select exactly ${originalCount} elements (currently have ${classCount})`;
          
        toast.show({
          title: "Change Elements",
          content: errorMessage,
          duration: 10000,
          type: "fail"
        });
      }
    }
  }

  const performUKCElementChange = () => {
    if (props.event) {
      let _shows = JSON.parse(JSON.stringify(shows));
      const isFastCat = props.registration.show_type === Constants.show_type.fast_cat;

      let _payload:any = {
        dog_classes: [],
        update_show_registration: updateUKCRegistration // Add flag for backend
      }

      // For Fast Cat removal, we need to track which scores to keep vs remove
      if (isFastCat) {
        const selectedScoreIds = new Set<string>();
        
        // Collect all selected score IDs
        for (let s in _shows) {
          for (let c in _shows[s].dog_classes) {
            if (_shows[s].dog_classes[c].score_id) {
              selectedScoreIds.add(_shows[s].dog_classes[c].score_id);
            }
          }
        }

        // Add all selected classes to payload
        for (let s in _shows) {
          for (let c in _shows[s].dog_classes) {
            let _dog_class:any = {
              score_id: _shows[s].dog_classes[c].score_id,
              show_id: _shows[s]._id,
              dog_class: {
                section: _shows[s].dog_classes[c].section,
                show_element: _shows[s].dog_classes[c].show_element,
                level: _shows[s].dog_classes[c].level
              }
            };

            // Include time_slot if it exists (for FastCAT, Fetch, etc.)
            if (_shows[s].dog_classes[c].time_slot) {
              _dog_class.dog_class.time_slot = _shows[s].dog_classes[c].time_slot;
            }

            _payload.dog_classes.push(_dog_class);
          }
        }

        // For Fast Cat, add removal instructions for unselected scores
        const removedScoreIds = scoreIds.filter(id => !selectedScoreIds.has(id));
        if (removedScoreIds.length > 0) {
          _payload.remove_score_ids = removedScoreIds;
        }
      } else {
        // Non-Fast Cat logic: Track which score_ids are used and assign unused ones to new classes
        const usedScoreIds = new Set<string>();
        const classesNeedingScoreIds: any[] = [];
        
        // First pass: collect classes with existing score_ids and identify those without
        for (let s in _shows) {
          for (let c in _shows[s].dog_classes) {
            const dogClass = _shows[s].dog_classes[c];
            
            // Skip invalid classes (no show_element or level)
            if (!dogClass.show_element || !dogClass.level) {
              continue;
            }
            
            if (dogClass.score_id) {
              usedScoreIds.add(dogClass.score_id);
            } else {
              // This class needs a score_id assigned
              classesNeedingScoreIds.push({
                showId: _shows[s]._id,
                dogClass: dogClass
              });
            }
          }
        }
        
        // Find unused score_ids from the original set
        const unusedScoreIds = originalScoreIds.filter(id => !usedScoreIds.has(id));
        
        // Assign unused score_ids to classes that need them
        classesNeedingScoreIds.forEach((item, index) => {
          if (index < unusedScoreIds.length) {
            item.dogClass.score_id = unusedScoreIds[index];
          }
        });
        
        // Second pass: build the payload with all classes now having score_ids
        for (let s in _shows) {
          for (let c in _shows[s].dog_classes) {
            const dogClass = _shows[s].dog_classes[c];
            
            // Skip invalid classes
            if (!dogClass.show_element || !dogClass.level) {
              continue;
            }
            
            let _dog_class:any = {
              score_id: dogClass.score_id,
              show_id: _shows[s]._id,
              dog_class: {
                section: dogClass.section,
                show_element: dogClass.show_element,
                level: dogClass.level
              }
            };

            // Include time_slot if it exists (for FastCAT, Fetch, etc.)
            if (dogClass.time_slot) {
              _dog_class.dog_class.time_slot = dogClass.time_slot;
            }

            _payload.dog_classes.push(_dog_class);
          }
        }
      }

      apiService.dogClassUpdates(props.event._id, _payload).then((response) => {
        if (response === undefined) {
          toast.show({
            title: "Change Elements",
            content: "Element change for " + props.registration.dog.call_name + " successful",
            duration: 10000,
            type: "success"
          });

          props.onComplete();
        }
      }).catch((e: AxiosError) => {
        toast.show({
          title: "Change Elements",
          content: "Failed to change elements for " + props.registration.dog.call_name,
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
      });
    }
  }

  const performAKCElementChange = () => {
    if (props.event && shows) {
      let _shows = JSON.parse(JSON.stringify(shows));
      const isFastCat = props.registration.show_type === Constants.show_type.fast_cat;

      let _payload:any = {
        dog_classes: []
      }

      // For Fast Cat removal, we need to track which scores to keep vs remove
      if (isFastCat) {
        const selectedScoreIds = new Set<string>();
        
        // Collect all selected score IDs
        for (let s in _shows) {
          for (let c in _shows[s].dog_classes) {
            if (_shows[s].dog_classes[c].score_id) {
              selectedScoreIds.add(_shows[s].dog_classes[c].score_id);
            }
          }
        }

        // For Fast Cat, add removal instructions for unselected scores
        const removedScoreIds = scoreIds.filter(id => !selectedScoreIds.has(id));
        if (removedScoreIds.length > 0) {
          _payload.remove_score_ids = removedScoreIds;
        }
      }

      // Track which score_ids are used and assign unused ones to new classes (same as UKC logic)
      const usedScoreIds = new Set<string>();
      const classesNeedingScoreIds: any[] = [];
      
      // First pass: collect classes with existing score_ids and identify those without
      for (let s in _shows) {
        for (let c in _shows[s].dog_classes) {
          const dogClass = _shows[s].dog_classes[c];
          
          // Skip invalid classes (no show_element or level)
          if (!dogClass.show_element || !dogClass.level) {
            continue;
          }
          
          if (dogClass.score_id) {
            usedScoreIds.add(dogClass.score_id);
          } else {
            // This class needs a score_id assigned
            classesNeedingScoreIds.push({
              showId: _shows[s]._id,
              dogClass: dogClass
            });
          }
        }
      }
      
      // Find unused score_ids from the original set
      const unusedScoreIds = originalScoreIds.filter(id => !usedScoreIds.has(id));
      
      // Assign unused score_ids to classes that need them
      classesNeedingScoreIds.forEach((item, index) => {
        if (index < unusedScoreIds.length) {
          item.dogClass.score_id = unusedScoreIds[index];
        }
      });

      // Second pass: build the payload
      for (let s in _shows) {
        for (let c in _shows[s].dog_classes) {
          const dogClass = _shows[s].dog_classes[c];
          
          // Skip invalid classes
          if (!dogClass.show_element || !dogClass.level) {
            continue;
          }
          
          let _dog_class:any = {
            score_id: dogClass.score_id,
            show_id: _shows[s]._id,
            dog_class: {
              section: dogClass.section,
              show_element: dogClass.show_element,
              level: dogClass.level
            }
          };

          // Include time_slot if it exists (for FastCAT, Fetch, etc.)
          if (dogClass.time_slot) {
            _dog_class.dog_class.time_slot = dogClass.time_slot;
          }

          // HOTFIX: Include critical fields that were missing from recreation
          // These fields are needed to preserve arm_number and other data during AKC element changes
          
          // Include arm_number if it exists (critical for scoring)
          if (dogClass.arm_number) {
            _dog_class.arm_number = dogClass.arm_number;
          }
          
          // Include plan_to_earn_title from registration
          if (props.registration.plan_to_earn_title !== undefined) {
            _dog_class.plan_to_earn_title = props.registration.plan_to_earn_title;
          }
          
          // Include event_number from registration
          if (props.registration.event_number) {
            _dog_class.event_number = props.registration.event_number;
          }
          
          // Include using_agent from registration
          if (props.registration.using_agent !== undefined) {
            _dog_class.using_agent = props.registration.using_agent;
          }
          
          // Include fee and waitlist in dog_class if they exist
          if (dogClass.fee !== undefined) {
            _dog_class.dog_class.fee = dogClass.fee;
          }
          
          if (dogClass.waitlist !== undefined) {
            _dog_class.dog_class.waitlist = dogClass.waitlist;
          }

          _payload.dog_classes.push(_dog_class);
        }
      }

      apiService.dogClassUpdatesAKC(props.event._id, _payload).then((response) => {
        if (response === undefined) {
          toast.show({
            title: "Change Elements",
            content: "Element change for " + props.registration.dog.call_name + " successful",
            duration: 10000,
            type: "success"
          });

          props.onComplete();
        }
      }).catch((e: AxiosError) => {
        toast.show({
          title: "Change Elements",
          content: "Failed to change elements for " + props.registration.dog.call_name,
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
        setShowConfirmationModal(false);
      });
    } else {
      setShowConfirmationModal(false);
    }
  }

  useEffect(() => {
    if (!loaded) {
      apiService.getClubEventShows(props.registration.club_id, props.registration.event_id, {
        query: {
          $and: []
        }
      }).then((response) => {
        if (response.length > 0) {
          let _shows:IShowClass[] = [];

          for (let s in response) {
            let _show:IShowClass = { ...response[s], dog_classes: [] };
            _shows.push(_show);
          }

          // setShow(response[0]);

          let _query:any = {
            query: {
              "$and": [{
                attribute_name: "dog_id",
                attribute_value: {
                  operator: "$eq",
                  value: props.registration.dog_id
                }
              }]
            },
            sort: []
          };

          apiService.getEventScores(props.registration.event_id, _query).then((scoreResponse) => {
            let scoreIds:string[] = [];

            for (let i in scoreResponse) {
              for (let s in _shows) {
                if (_shows[s]._id === scoreResponse[i].show_id && scoreResponse[i].dog_class) {
                  // For FastCAT and other timeslot shows, preserve the time_slot from the score
                  const dogClassData: any = { 
                    ...scoreResponse[i].dog_class, 
                    id: Math.random().toString(36).substring(2, 8), 
                    score_id: scoreResponse[i]._id 
                  };
                  
                  // Add time_slot if it exists on the score
                  if (scoreResponse[i].time_slot) {
                    dogClassData.time_slot = scoreResponse[i].time_slot;
                  }
                  
                  // HOTFIX: Preserve critical fields from original score for AKC element changes
                  // These fields need to be preserved so they can be included in the payload
                  if (scoreResponse[i].arm_number) {
                    dogClassData.arm_number = scoreResponse[i].arm_number;
                  }
                  
                  // Preserve fee and waitlist from original dog_class if they exist
                  if (scoreResponse[i].dog_class.fee !== undefined) {
                    dogClassData.fee = scoreResponse[i].dog_class.fee;
                  }
                  
                  if (scoreResponse[i].dog_class.waitlist !== undefined) {
                    dogClassData.waitlist = scoreResponse[i].dog_class.waitlist;
                  }
                  
                  _shows[s].dog_classes?.push(dogClassData);
                  break;
                }
              }

              scoreIds.push(scoreResponse[i]._id);
            }

            setScoreIds(scoreIds);
            setOriginalScoreIds(scoreIds); // Store the original count - this never changes
            setShows(_shows);
            setLoaded(true);
            
            // Trigger timeslot refresh for FastCAT and other timeslot shows
            setRefreshTimeslots(prev => prev + 1);
          }).catch((e: AxiosError) => {
            toast.show({
              title: "Get Dog Classes",
              content: "Failed to get Dog Classes",
              duration: 10000,
              errorDetails: e,
              type: "fail"
            });
          });
        }
      });
    }
  }, [loaded, props.registration, props.event?.sanctioning_club]);

  return (
    <>
      {showConfirmationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundColor: 'rgba(50, 50, 50, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000000
        }}>
          <div style={{
            backgroundColor: '#FFF',
            borderRadius: '20px',
            maxWidth: '600px',
            minWidth: '400px',
            maxHeight: '400px',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: '30px', right: '20px', cursor: 'pointer', fontSize: '24px' }} 
                 onClick={() => setShowConfirmationModal(false)}>×</div>
            <Confirm
              message={
                <>
                  <p><strong>Warning:</strong> Updating elements for AKC/PWDCA/ABI events will reset any associated scores. If results have already been entered for an entry you're modifying, those results will be cleared and must be re-entered.</p>
                </>
              }
              onConfirm={performAKCElementChange}
              onCancel={() => setShowConfirmationModal(false)}
            />
          </div>
        </div>
      )}
      {showUKCConfirmationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          backgroundColor: 'rgba(50, 50, 50, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000000
        }}>
          <div style={{
            backgroundColor: '#FFF',
            borderRadius: '20px',
            maxWidth: '600px',
            minWidth: '400px',
            maxHeight: '400px',
            position: 'relative'
          }}>
            <div style={{ position: 'absolute', top: '30px', right: '20px', cursor: 'pointer', fontSize: '24px' }} 
                 onClick={() => setShowUKCConfirmationModal(false)}>×</div>
            <div className="Confirm">
              <div className="title">Confirm Changes</div>
              <p>Would you like to update the initial registration too?</p>
              <p style={{ fontSize: '14px', marginTop: '10px' }}>
                If you have a UKC Change Form to accompany these changes, uncheck this box so the initial registration remains intact.
              </p>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={updateUKCRegistration}
                    onChange={(e) => setUpdateUKCRegistration(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>Update initial registration</span>
                </label>
              </div>
              <div className="button-group">
                <Button onClick={() => setShowUKCConfirmationModal(false)}>Cancel</Button>
                <Button onClick={() => {
                  setShowUKCConfirmationModal(false);
                  performUKCElementChange();
                }}>Confirm</Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="ChangeElementsForm">
        <form className="ChangeElements" onSubmit={(e) => { e.preventDefault(); if (isValidClasses()) { submitChange(); } }}>
          <div className="title">
            <span>Change Elements</span>
            <span>{props.registration.dog.call_name}</span>
          </div>
          <div className="forms">
            {loaded && shows && shows.length > 0 && shows.map((s: IShowClass, index: number) => {
              return(
                <div key={index + "form"} className="formContainer">
                  <div className="formTitle">{s.show_type +" "+ (s.custom_show_name || s.show_name) + " (" + moment(s.show_date).format("MMMM Do") +")"}</div>
                  {s.dog_classes && <FormBuilder 
                    show={s}
                    event={props.event}
                    dog={props.registration.dog}
                    classes={s.dog_classes}
                    edit={true}
                    overrideDisable={true}
                    refreshTimeslots={refreshTimeslots}
                    onChange={
                      (newClasses: IDogClassElement[]) => {
                        const targetShowId = s._id;
                        const targetShowName = s.show_name;
                        
                        let _newClasses:IDogClassElement[] = JSON.parse(JSON.stringify(newClasses));

                        // Use functional state update to avoid stale closure issues
                        setShows((currentShows) => {
                          let _shows = JSON.parse(JSON.stringify(currentShows));
                          
                          for (let i in _shows) {
                            if (_shows[i]._id === targetShowId) {
                              _shows[i].dog_classes = _newClasses;
                              break;
                            }
                          }

                          // Calculate scoresInUse from the updated shows
                          let scoresInUse:string[] = [];
                          for (let i in _shows) {
                            for (let c in _shows[i].dog_classes) {
                              if (_shows[i].dog_classes[c].score_id) {
                                scoresInUse.push(_shows[i].dog_classes[c].score_id);
                              }
                            }
                          }

                          setScoreIds(scoresInUse);
                          
                          return _shows;
                        });
                      }
                    }
                  />}
                </div>
              );
            })}
          </div>
          <div className="actions">
            <Button type="button" onClick={() => { props.onClose(); }}>Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ChangeElements;
